import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonIcon,
  IonSpinner,
  IonToast,
  IonItem,
  IonBackButton,
  IonButtons,
  IonHeader,
  IonToolbar
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mailOutline, arrowBackOutline } from 'ionicons/icons';
import { useAuth } from '../contexts/AuthContext';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { resetPassword, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await resetPassword(email);
      setSuccessMessage(t('auth.resetEmailSent'));
      setShowToast(true);
      setTimeout(() => {
        history.push('/login');
      }, 3000);
    } catch (error) {
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader className="bg-transparent absolute">
        <IonToolbar className="bg-transparent">
          <IonButtons slot="start">
            <IonButton onClick={() => history.push('/login')} className="text-white">
              <IonIcon icon={arrowBackOutline} className="text-2xl" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      
      <IonContent fullscreen className="bg-gradient-to-br from-primary-600 to-purple-700">
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
          <div className="max-w-md w-full">
            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-2xl rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-500 to-primary-700 text-white px-8 py-6">
                <h1 className="text-3xl font-bold text-center mb-2">
                  {t('auth.resetPassword')}
                </h1>
                <p className="text-lg text-center opacity-90">
                  {t('auth.forgotPasswordDescription') || 'Enter your email to reset your password'}
                </p>
              </div>
              
              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div className="relative group">
                      <IonItem lines="none" className="border-2 border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 hover:border-primary-400 focus-within:border-primary-500 focus-within:shadow-lg">
                        <IonIcon 
                          icon={mailOutline} 
                          slot="start"
                          className="text-gray-500 dark:text-gray-400 mr-3"
                        />
                        <IonInput
                          type="email"
                          value={email}
                          onIonChange={(e) => setEmail(e.detail.value!)}
                          placeholder={t('auth.email')}
                          required
                          autocomplete="email"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      </IonItem>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <IonButton
                      expand="block"
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transform transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <IonSpinner name="crescent" className="w-5 h-5" />
                      ) : (
                        t('auth.resetPassword')
                      )}
                    </IonButton>
                  </div>

                  <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                    <a 
                      href="/login" 
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                      {t('common.back')} {t('auth.login')}
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => {
            setShowToast(false);
            clearError();
            setSuccessMessage('');
          }}
          message={successMessage || error || t('auth.resetEmailError')}
          duration={3000}
          color={successMessage ? 'success' : 'danger'}
          position="top"
        />
      </IonContent>
    </IonPage>
  );
};

export default ForgotPassword;